import React, { useEffect, useState, useRef } from "react";
import {
  Box, Typography, CircularProgress, IconButton, Modal, Snackbar, Alert
} from "@mui/material";
import { useApi } from "../api/ApiContext";
import { useParams } from "react-router-dom";
import DownloadIcon from "@mui/icons-material/Download";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import CloseIcon from "@mui/icons-material/Close";
import FavoriteBorderRoundedIcon from "@mui/icons-material/FavoriteBorderRounded";
import { motion, AnimatePresence } from "framer-motion";

const ExpandableText = ({ text, limit = 120, sx = {} }) => {
  const [expanded, setExpanded] = useState(false);
  if (!text) return null;
  const isLong = text.length > limit;
  const displayText = !isLong || expanded ? text : text.slice(0, limit) + "...";
  return (
    <Typography sx={{ ...sx, wordBreak: "break-word" }}>
      {displayText}
      {isLong && (
        <Box
          component="span"
          sx={{ ml: 0.8, color: "#EB578A", fontWeight: 700, fontSize: 13, cursor: "pointer", textDecoration: "underline", userSelect: "none" }}
          onClick={e => { e.stopPropagation(); setExpanded(v => !v); }}
        >
          {expanded ? " Mniej" : " Więcej"}
        </Box>
      )}
    </Typography>
  );
};

function getImageUrl(imagePath) {
  if (!imagePath) return "";
  return imagePath.startsWith("http")
    ? imagePath
    : `https://ksiega.kalmarry.pl/${imagePath.replace(/^\/+/, "")}`;
}

const slideVariants = {
  enter: (dir) => ({ y: dir > 0 ? 500 : -500, opacity: 0, scale: 0.97 }),
  center: { y: 0, opacity: 1, scale: 1, transition: { duration: 0.35, ease: [0.4, 0, 0.2, 1] } },
  exit: (dir) => ({ y: dir > 0 ? -500 : 500, opacity: 0, scale: 0.97, transition: { duration: 0.18 } }),
};

const SharedFavorites = () => {
  const { token } = useParams();
  const api = useApi();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalIdx, setModalIdx] = useState(0);
  const [direction, setDirection] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [toast, setToast] = useState({ open: false, msg: "", severity: "success" });
  const touchStartY = useRef(null);
  const modalRef = useRef();

  const showToast = (msg, severity = "success") => setToast({ open: true, msg, severity });

  useEffect(() => {
    setLoading(true);
    api.get(`/get_shared_favorites.php?token=${token}`)
      .then(res => {
        const arr = Array.isArray(res.data) ? res.data : [];
        setImages(arr.map(img => ({ ...img, url: getImageUrl(img.image_path) })));
      })
      .catch(() => showToast("Nie udało się załadować zdjęć.", "error"))
      .finally(() => setLoading(false));
  }, [token, api]);

  useEffect(() => {
    document.body.style.overflow = modalOpen ? "hidden" : "";
    document.body.style.touchAction = modalOpen ? "none" : "";
    return () => { document.body.style.overflow = ""; document.body.style.touchAction = ""; };
  }, [modalOpen]);

  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!(document.fullscreenElement || document.webkitFullscreenElement));
    document.addEventListener("fullscreenchange", onFsChange);
    document.addEventListener("webkitfullscreenchange", onFsChange);
    return () => {
      document.removeEventListener("fullscreenchange", onFsChange);
      document.removeEventListener("webkitfullscreenchange", onFsChange);
    };
  }, []);

  // Browser back closes modal
  useEffect(() => {
    if (!modalOpen) return;
    window.history.pushState({ modal: true }, "");
    const onPop = () => setModalOpen(false);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [modalOpen]);

  const handleDownloadImage = async (img) => {
    if (!img?.image_path) return;
    try {
      const response = await fetch(getImageUrl(img.image_path), { mode: "cors" });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `image_${img.zdjecie_id || Date.now()}.jpg`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch {
      showToast("Nie udało się pobrać zdjęcia.", "error");
    }
  };

  const showPrev = () => { if (modalIdx > 0) { setDirection(-1); setModalIdx(i => i - 1); } };
  const showNext = () => { if (modalIdx < images.length - 1) { setDirection(1); setModalIdx(i => i + 1); } };

  const handleTouchStart = (e) => { touchStartY.current = e.touches[0].clientY; };
  const handleTouchEnd = (e) => {
    if (touchStartY.current === null) return;
    const dy = e.changedTouches[0].clientY - touchStartY.current;
    if (dy > 60) showPrev();
    else if (dy < -60) showNext();
    touchStartY.current = null;
  };
  const handleWheel = (e) => { if (e.deltaY > 0) showNext(); else if (e.deltaY < 0) showPrev(); };

  useEffect(() => {
    if (!modalOpen) return;
    const onKey = (e) => {
      if (e.key === "ArrowUp" || e.key === "ArrowLeft") showPrev();
      if (e.key === "ArrowDown" || e.key === "ArrowRight") showNext();
      if (e.key === "Escape") setModalOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line
  }, [modalOpen, modalIdx, images.length]);

  const enterFullscreen = () => {
    if (modalRef.current?.requestFullscreen) modalRef.current.requestFullscreen();
    else if (modalRef.current?.webkitRequestFullscreen) modalRef.current.webkitRequestFullscreen();
  };
  const exitFullscreen = () => {
    if (document.fullscreenElement) document.exitFullscreen();
    else if (document.webkitFullscreenElement) document.webkitExitFullscreen();
  };

  const currentImg = images[modalIdx];

  return (
    <Box sx={{ width: "100vw", minHeight: "100vh", bgcolor: "#fff8fb", overflowX: "hidden" }}>
      <Box sx={{ width: "100%", maxWidth: 960, mx: "auto", pt: 3, pb: 6, px: { xs: 1, sm: 2 } }}>
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
          <FavoriteIcon sx={{ color: "#EB578A", fontSize: 26 }} />
          <Typography variant="h5" sx={{ fontWeight: 800, color: "#1a1a2e" }}>
            Udostępnione ulubione
          </Typography>
          {images.length > 0 && (
            <Box sx={{ bgcolor: "#EB578A", color: "#fff", borderRadius: "50px", px: 1.2, py: 0.2, fontSize: 13, fontWeight: 700, ml: 0.5 }}>
              {images.length}
            </Box>
          )}
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
            <CircularProgress />
          </Box>
        ) : images.length === 0 ? (
          <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="50vh" gap={2}>
            <FavoriteBorderRoundedIcon sx={{ fontSize: 72, color: "#f0c6d8" }} />
            <Typography variant="h6" sx={{ color: "#ccc", fontWeight: 600 }}>Brak zdjęć</Typography>
          </Box>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "repeat(3, 1fr)", sm: "repeat(3, 1fr)", md: "repeat(4, 1fr)" },
              gap: { xs: "2px", sm: "3px" },
            }}
          >
            {images.map((img, idx) => (
              <Box
                key={img.zdjecie_id || idx}
                onClick={() => { setModalIdx(idx); setDirection(0); setModalOpen(true); }}
                sx={{
                  width: "100%", aspectRatio: "1 / 1", overflow: "hidden",
                  position: "relative", cursor: "pointer", bgcolor: "#f5e8ef",
                  "&:hover img": { transform: "scale(1.04)" },
                  "&:hover .fav-badge": { opacity: 1 },
                }}
              >
                <img
                  src={img.url}
                  alt={img.text || ""}
                  style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", transition: "transform 0.35s ease" }}
                  draggable={false}
                  loading="lazy"
                />
                <Box
                  className="fav-badge"
                  sx={{
                    position: "absolute", top: 5, right: 5,
                    bgcolor: "rgba(255,255,255,0.9)", borderRadius: "50%",
                    width: 26, height: 26, display: "flex", alignItems: "center", justifyContent: "center",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.12)",
                    opacity: 0, transition: "opacity 0.2s",
                  }}
                >
                  <FavoriteIcon sx={{ fontSize: 14, color: "#e53935" }} />
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* MODAL */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
        <Box
          sx={{ position: "fixed", inset: 0, bgcolor: "#fff", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1300, overflow: "hidden" }}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          onWheel={handleWheel}
        >
          <IconButton
            onClick={() => setModalOpen(false)}
            sx={{
              position: "fixed", top: 14, right: 14, zIndex: 10,
              bgcolor: "rgba(255,255,255,0.95)", boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              "&:hover": { bgcolor: "#fff" },
            }}
          >
            <CloseIcon />
          </IconButton>

          <AnimatePresence initial={false} custom={direction} mode="wait">
            <motion.div
              ref={modalRef}
              key={modalIdx}
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              style={{
                width: "100vw", height: "100vh",
                background: "#fff",
                display: "flex", flexDirection: "column",
                alignItems: "center", justifyContent: "center",
                position: "relative", overflow: "hidden",
              }}
            >
              {/* Fullscreen toggle */}
              <IconButton
                onClick={isFullscreen ? exitFullscreen : enterFullscreen}
                sx={{
                  position: "absolute", top: 14, right: 60, zIndex: 5,
                  bgcolor: "rgba(245,245,245,0.92)",
                  "&:hover": { bgcolor: "rgba(235,87,138,0.1)" },
                }}
              >
                {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
              </IconButton>

              {/* Image */}
              <Box sx={{ flex: 1, width: "100%", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
                {currentImg && (
                  <img
                    src={currentImg.url}
                    alt={currentImg.text || ""}
                    style={{ width: "100%", height: "100%", maxHeight: "80vh", objectFit: "contain", display: "block" }}
                    draggable={false}
                  />
                )}
              </Box>

              {/* Info bar */}
              <Box
                sx={{
                  width: "100%",
                  px: { xs: 2, sm: 5 },
                  pb: { xs: "calc(env(safe-area-inset-bottom, 24px) + 16px)", sm: 4 },
                  pt: 2,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  background: "linear-gradient(0deg, rgba(255,255,255,0.98) 80%, rgba(255,255,255,0.01) 100%)",
                  position: "absolute",
                  bottom: 0, left: 0, zIndex: 2,
                }}
              >
                <ExpandableText text={currentImg?.text} limit={120} sx={{ fontSize: 16, fontWeight: 600, mb: 0.5, color: "#1a1a2e" }} />
                {currentImg?.additional_text && (
                  <Typography sx={{ fontSize: 14, color: "#888", mb: 1 }}>
                    {currentImg.additional_text}
                  </Typography>
                )}
                <Typography sx={{ fontSize: 13, color: "#ccc", mb: 1.5, fontWeight: 500 }}>
                  {modalIdx + 1} / {images.length}
                </Typography>
                <IconButton
                  onClick={() => handleDownloadImage(currentImg)}
                  sx={{ bgcolor: "#f5f5f5", color: "#444", border: "1.5px solid #eee", "&:hover": { bgcolor: "#f0f0f0" } }}
                  aria-label="Pobierz zdjęcie"
                >
                  <DownloadIcon />
                </IconButton>
              </Box>
            </motion.div>
          </AnimatePresence>
        </Box>
      </Modal>

      <Snackbar
        open={toast.open}
        autoHideDuration={2400}
        onClose={() => setToast(t => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={toast.severity} onClose={() => setToast(t => ({ ...t, open: false }))} sx={{ borderRadius: 3, fontWeight: 600 }}>
          {toast.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SharedFavorites;
